import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { bs } from '../../../../styles/styles';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../../HoverCard';
import { Tag } from '../../../tags/Tag';
import { styleSheet } from './styles';

/**
 * Accepts a contactFilter and returns a list of tag strings grouped by filter operator (And, Or, Not).
 *
 * Walks the whole criteria tree: audiences built by the AI assistant and AI campaigns nest the chosen tags inside
 * wrapper groups, so a one-level scan loses them while surfacing the system exclusion tags under the wrong operator.
 * A tag's own operator wins over the group it sits in, so an excluded tag inside an And group still reads as Not.
 */
const getTagGroupingFromContactFilterRequest = (filterRequest: Api.ContactFilter) => {
	const groupedTags = {
		[Api.FilterOperator.And]: [],
		[Api.FilterOperator.Or]: [],
		[Api.FilterOperator.Not]: [],
	} as Record<Api.FilterOperator, string[]>;

	const bucketFor = (op: Api.FilterOperator | Api.ContactFilterOperator | undefined): Api.FilterOperator =>
		op === Api.FilterOperator.Or
			? Api.FilterOperator.Or
			: op === Api.FilterOperator.Not
				? Api.FilterOperator.Not
				: Api.FilterOperator.And;

	const collect = (criteria: Api.IContactFilterCriteria[] | undefined, inheritedOp: Api.FilterOperator) => {
		for (const criterion of criteria ?? []) {
			const effectiveOp = bucketFor(criterion.op ?? inheritedOp);
			if (criterion.property === Api.ContactFilterCriteriaProperty.Tag && criterion.value) {
				if (!groupedTags[effectiveOp].includes(criterion.value)) {
					groupedTags[effectiveOp].push(criterion.value);
				}
			}
			collect(criterion.criteria, effectiveOp);
		}
	};

	collect(filterRequest.criteria, bucketFor(filterRequest.op));
	return groupedTags;
};

function tagWithOperator(tag: string, operator: Api.FilterOperator) {
	return { tag, operator };
}

export const ContactFilterRequestTagList = ({
	filterRequest,
	maxTagsToDisplayInline,
	renderTag: renderTagProp,
	renderOverflowTag: renderOverflowTagProp,
	includeSystemExcludedTags,
}: {
	filterRequest: Api.ContactFilter;
	maxTagsToDisplayInline?: number;
	renderTag?: (tag: string, operator: Api.FilterOperator, index: number) => React.ReactNode;
	renderOverflowTag?: (tag: string, operator: Api.FilterOperator, index: number) => React.ReactNode;
	includeSystemExcludedTags?: boolean;
}) => {
	const operatorGroupedTags = getTagGroupingFromContactFilterRequest(filterRequest);
	if (!includeSystemExcludedTags) {
		operatorGroupedTags.Not = operatorGroupedTags.Not.filter(tag => !Api.DefaultBulkSendExcludedTags.has(tag));
	}
	const allTags = [
		operatorGroupedTags.Or.map(tag => tagWithOperator(tag, Api.FilterOperator.Or)),
		operatorGroupedTags.And.map(tag => tagWithOperator(tag, Api.FilterOperator.And)),
		operatorGroupedTags.Not.map(tag => tagWithOperator(tag, Api.FilterOperator.Not)),
	].flat();
	let inlineTags = allTags;
	let overflowTags: typeof allTags = [];

	if (maxTagsToDisplayInline != null) {
		inlineTags = allTags.slice(0, maxTagsToDisplayInline);
		overflowTags = allTags.slice(maxTagsToDisplayInline);
	}

	const renderTag = (tag: string, op: Api.FilterOperator, index: number) => {
		if (renderTagProp) {
			return renderTagProp(tag, op, index);
		}
		return (
			<div className={css(styleSheet.tagContainer)} key={`${op}-${tag}-${index}`}>
				{index > 0 && <span className={css(styleSheet.tagTokenOpPrefix)}>{op.toLowerCase()}</span>}
				<Tag
					styles={[
						bs.truncateText,
						styleSheet.tagToken,
						op === Api.FilterOperator.Not ? styleSheet.tagTokenExclude : null,
					]}
					tagValue={tag}
				/>
			</div>
		);
	};

	const renderOverflowTag = (tag: string, op: Api.FilterOperator, index: number) => {
		if (renderOverflowTagProp) {
			return renderOverflowTagProp(tag, op, index);
		}
		return (
			<div className={css(styleSheet.overflowTagContainer)} key={`${op}-${tag}-${index}`}>
				{index > 0 && <span className={css(styleSheet.tagTokenOpPrefix)}>{op.toLowerCase()}</span>}
				<Tag
					styles={[
						bs.truncateText,
						styleSheet.tagToken,
						op === Api.FilterOperator.Not ? styleSheet.tagTokenExclude : null,
					]}
					tagValue={tag}
				/>
			</div>
		);
	};

	return (
		<div className={css(styleSheet.tags)}>
			{inlineTags.map((tagWithOp, i) => {
				return renderTag(tagWithOp.tag, tagWithOp.operator, i);
			})}
			{overflowTags.length > 0 ? (
				<HoverCard openDelay={0}>
					<HoverCardTrigger className={css(bs.flexCenter)}>
						<span className={css(bs.brandLink, styleSheet.moreTags)}>
							+ {allTags.length - maxTagsToDisplayInline} more
						</span>
						&nbsp;
					</HoverCardTrigger>
					<HoverCardContent styles={[styleSheet.extraTags]}>
						{overflowTags.map((tagWithOp, i) => {
							return renderOverflowTag(tagWithOp.tag, tagWithOp.operator, i + maxTagsToDisplayInline);
						})}
					</HoverCardContent>
				</HoverCard>
			) : null}
		</div>
	);
};
